/**
 * Choix des simulateurs d'une licence d'abonnement Starter / Focus attribuée depuis le pool du
 * compte, PAR PERSONNE (règle `lib/licence-simulators.ts`, incident ALTIOS du 14/10/2026).
 * Ouvert à l'attribution (Mon compte : fiche, tableau, création d'un collaborateur) et depuis la
 * fiche pour modifier le choix.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { pickSimulatorDescription } from "../lib/simulator-description-i18n";

export interface SelectableSimulator {
  id: string;
  name: string;
  type: string | null;
  description?: string | null;
  descriptionFr?: string | null;
  descriptionEn?: string | null;
}

interface LicenceSimulatorsDialogProps {
  open: boolean;
  licenceLabel: string;
  collaboratorName: string;
  max: number;
  /** `null` = liste en cours de chargement ; erreur de chargement dans `loadError`. */
  simulators: SelectableSimulator[] | null;
  loadError?: string | null;
  initialIds: string[];
  onCancel: () => void;
  /** Enregistre le choix. Renvoie un message d'erreur, ou `null` si c'est fait. */
  onConfirm: (simulatorIds: string[]) => Promise<string | null>;
}

export function LicenceSimulatorsDialog({ open, licenceLabel, collaboratorName, max, simulators, loadError, initialIds, onCancel, onConfirm }: LicenceSimulatorsDialogProps) {
  const { t, i18n } = useTranslation();
  const [selected, setSelected] = useState<string[]>(initialIds);
  // Simulateur survolé (ou sélectionné au clavier / au doigt) : sa description s'affiche sous la liste.
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) { setSelected(initialIds); setError(null); setSaving(false); setHoveredId(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Un choix enregistré peut contenir un simulateur qui n'est plus proposé — un coach, inclus
  // d'office depuis le 14/10/2026 : invisible dans la liste, il occupait une place sans pouvoir être
  // décoché et faisait refuser l'enregistrement (`licence_simulators_unknown`). On ne garde que les
  // simulateurs proposés, dès que la liste est connue.
  useEffect(() => {
    if (!open || !simulators) return;
    const proposes = new Set(simulators.map((s) => s.id));
    setSelected((prev) => (prev.every((id) => proposes.has(id)) ? prev : prev.filter((id) => proposes.has(id))));
  }, [open, simulators]);

  const toggle = (id: string) => {
    setHoveredId(id);
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : prev.length >= max ? prev : [...prev, id]));
  };

  const confirm = async () => {
    setSaving(true);
    setError(null);
    const err = await onConfirm(selected);
    setSaving(false);
    if (err) setError(err);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onCancel(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("account.licenceSimulators.title", { licence: licenceLabel })}</DialogTitle>
          <DialogDescription>
            {t("account.licenceSimulators.description", { name: collaboratorName, count: max })}
          </DialogDescription>
        </DialogHeader>

        {loadError ? (
          <p role="alert" className="text-sm text-[#C0423F]">{loadError}</p>
        ) : simulators === null ? (
          <p className="text-sm text-muted-foreground"><Loader2 className="mr-1 inline h-4 w-4 animate-spin" />{t("common.loading")}</p>
        ) : simulators.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("account.licenceSimulators.none")}</p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">{t("account.licenceSimulators.counter", { count: selected.length, max })}</p>
            <p className="text-xs text-muted-foreground">{t("account.coachesIncluded")}</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 max-h-[50vh] overflow-y-auto" data-testid="licence-simulators-choices">
              {simulators.map((sim) => {
                const isSelected = selected.includes(sim.id);
                const maxReached = !isSelected && selected.length >= max;
                const desc = pickSimulatorDescription(sim, i18n.language);
                return (
                  <button
                    key={sim.id}
                    type="button"
                    title={desc || undefined}
                    aria-pressed={isSelected}
                    onMouseEnter={() => setHoveredId(sim.id)}
                    onFocus={() => setHoveredId(sim.id)}
                    disabled={maxReached || saving}
                    onClick={() => toggle(sim.id)}
                    className={`flex items-start gap-2 rounded-lg border-2 p-2 text-left text-sm transition-all ${
                      isSelected
                        ? "border-[#5A4BBE] bg-[#988EF2]/15"
                        : maxReached
                          ? "cursor-not-allowed border-gray-100 bg-gray-50 opacity-40"
                          : "border-border hover:border-[#988EF2]"
                    }`}
                  >
                    <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${isSelected ? "border-[#5A4BBE] bg-[#5A4BBE] text-white" : "border-gray-300"}`}>
                      {isSelected && <Check className="h-3 w-3" />}
                    </span>
                    <span>
                      <span className="block font-medium text-[#384872]">{sim.name}</span>
                      <span className="block text-xs text-muted-foreground">{sim.type === "coach" ? t("account.coach") : t("account.simulator")}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            {/* Qui est ce simulateur : description de celui survolé (ou touché, sur mobile). */}
            {(() => {
              const sim = simulators.find((s) => s.id === hoveredId);
              const desc = sim ? pickSimulatorDescription(sim, i18n.language) : "";
              return (
                <div className="min-h-[4.5rem] rounded-lg bg-[#A6D3F9]/20 px-3 py-2 text-xs text-[#384872]" aria-live="polite" data-testid="licence-simulators-description">
                  {sim ? (
                    <>
                      <span className="block font-semibold">{sim.name}</span>
                      <span className="line-clamp-4">{desc || t("account.licenceSimulators.noDescription")}</span>
                    </>
                  ) : (
                    <span className="text-muted-foreground">{t("account.licenceSimulators.hoverHint")}</span>
                  )}
                </div>
              );
            })()}
          </>
        )}

        {error && <p role="alert" className="text-sm text-[#C0423F]">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onCancel} disabled={saving}>{t("common.cancel")}</Button>
          <Button onClick={() => void confirm()} disabled={saving || selected.length === 0 || !simulators?.length}>
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {t("common.confirm")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
